import { setCookie, getCookie } from '../toolbox/cookie';

const DO_NOT_TRACKED_COOKIE = 'dw_dnt';
const RECENTLY_VIEWED_ENTRY = 'recentlyViewed';
const RECENTLY_VIEWED_ITEMS_LIMIT = 12;

/**
 * @description Base RecentlyViewedMixin implementation
 * @param ProductDetail widget for extending
 * @returns Recently Viewed Mixin
 */
function RecentlyViewedMixinClassCreator(ProductDetail: import('widgets/product/ProductDetail').TProductDetail) {
    /**
     * @category widgets
     * @subcategory product
     * @class RecentlyViewedMixin
     * @augments ProductDetail
     * @classdesc Represents RecentlyViewed functionality.
     * Uses only if Einstein recently viewed functionality for some reason cannot be used. Implement next features:
     * 1. Checks if products tracking allowed
     * 2. Stores product IDs (based on visited pages) in the cookie `recentlyViewed` if tracking allowed
     * @property {boolean} data-recently-viewed-tracking-allowed - determines if recently view tracking allowed based on preference
     * @property {string} data-master-pid - master product ID
     */
    class RecentlyViewedMixin extends ProductDetail {
        prefs() {
            return {
                recentlyViewedTrackingAllowed: false,
                masterPid: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.trackViewedItem(this.prefs().masterPid);
        }

        /**
         * @description Determines if recently viewed tracking allowed
         * @returns result
         */
        isTrackingAllowed(): boolean {
            return (Number(getCookie(DO_NOT_TRACKED_COOKIE)) !== 1) && this.prefs().recentlyViewedTrackingAllowed;
        }

        /**
         * @description Saves visited product IDs to cookies
         * @param arr - array with product IDs of visited products
         */
        saveViewedItems(arr: Array<string> = []) {
            setCookie(RECENTLY_VIEWED_ENTRY, arr.join('|'), 0, true);
        }

        /**
         * @description Parses visited product IDs from the string
         * @param str - string with product IDs
         * @returns result - array with product IDs
         */
        getViewedItems(str = ''): Array<string> {
            if (!str) {
                return [];
            }

            return str.split('|');
        }

        /**
         * @description Saves visited product to cookies
         * @param pid - visited master product ID
         */
        trackViewedItem(pid = '') {
            if (!pid || !this.isTrackingAllowed()) {
                return;
            }

            let trackedProducts = this.getViewedItems(getCookie(RECENTLY_VIEWED_ENTRY));

            trackedProducts = trackedProducts.filter(viewedItem => viewedItem !== pid);

            trackedProducts.unshift(pid);

            if (trackedProducts.length > RECENTLY_VIEWED_ITEMS_LIMIT) {
                trackedProducts = trackedProducts.slice(1, RECENTLY_VIEWED_ITEMS_LIMIT);
            }

            this.saveViewedItems(trackedProducts);
        }
    }

    return RecentlyViewedMixin;
}

export type TRecentlyViewedMixin = ReturnType<typeof RecentlyViewedMixinClassCreator>;

export type TRecentlyViewedMixinInstance = InstanceType<TRecentlyViewedMixin>;

export default RecentlyViewedMixinClassCreator;
